import React from "react"
import { useTranslation } from "react-i18next"
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Textarea,
  Label,
} from "@f2e/ui/components/base"
import { CopilotHiddenField } from "@/components/Copilot/CopilotHiddenField"

interface AddCaseModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (e: React.FormEvent) => void
  addCaseForm: {
    input: string
    expected_output: string
    note: string
  }
  onChange: (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void
  addCaseError: string | null
  addCaseLoading: boolean
}

const AddCaseModal = ({
  isOpen,
  onClose,
  onSubmit,
  addCaseForm,
  onChange,
  addCaseError,
  addCaseLoading,
}: AddCaseModalProps) => {
  const { t } = useTranslation()

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {t("evaluation.evaluationTable.action.addEvaluationCase")}
          </DialogTitle>
        </DialogHeader>
        <CopilotHiddenField
          fieldKey="addCase.summary"
          value={`inputLen=${(addCaseForm.input || "").length}; expectedLen=${(addCaseForm.expected_output || "").length}; loading=${addCaseLoading ? "true" : "false"}`}
        />
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="input">
              {t("evaluation.evaluationTable.form.input")}{" "}
              <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="input"
              name="input"
              value={addCaseForm.input}
              onChange={onChange}
              placeholder={t(
                "evaluation.evaluationTable.form.inputPlaceholder"
              )}
              className="max-h-48 overflow-auto whitespace-pre-wrap break-words"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="expected_output">
              {t("evaluation.evaluationTable.form.expectedOutput")}
            </Label>
            <Textarea
              id="expected_output"
              name="expected_output"
              value={addCaseForm.expected_output}
              onChange={onChange}
              placeholder={t(
                "evaluation.evaluationTable.form.expectedOutputPlaceholder"
              )}
              className="max-h-48 overflow-auto whitespace-pre-wrap break-words"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="note">
              {t("evaluation.evaluationTable.form.note")}
            </Label>
            <Textarea
              id="note"
              name="note"
              value={addCaseForm.note}
              onChange={onChange}
              placeholder={t("evaluation.evaluationTable.form.notePlaceholder")}
              className="max-h-48 overflow-auto whitespace-pre-wrap break-words"
            />
          </div>
          {addCaseError && (
            <div className="text-red-500 dark:text-red-300 text-sm">
              {addCaseError}
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={addCaseLoading}
            >
              {t("evaluation.evaluationTable.button.cancel")}
            </Button>
            <Button type="submit" disabled={addCaseLoading}>
              {addCaseLoading
                ? t("evaluation.evaluationTable.button.adding")
                : t("evaluation.evaluationTable.button.addCase")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default AddCaseModal
